/**
 * **The** panel on a phone — every one of them, bottom sheet, same box.
 *
 * `docs/list-filters.md` §4.5 drew it for the filter surfaces, from
 * `design/mockups/30-list-filters/html/filters-05-phones.html`; v3 §6 makes
 * it the rule for the lot, from `38-list-engine-v3/html/list-06-phone-sheets`
 * — the quick filter, the sheet a related-record field pushes over it, the
 * advanced builder, **List settings and its four panels**, the bulk menu and
 * the date picker. A popover hanging off a 32px toolbar button is a desktop
 * idea: on a 390px screen there is nowhere for it to hang, so it comes up
 * from the bottom instead, with the title, the live count and the one action
 * a thumb can reach.
 *
 * It was called `FilterSheet` while the filter was the only thing that used
 * it. The name is `PanelSheet` now because the design system's rule is "on a
 * phone every panel is the same bottom sheet", and a component called
 * *Filter*Sheet is one that the next panel's author does not think to reach
 * for. It is deliberately **not** `components/sheet.tsx` — that is the form
 * flyout (`record-flyouts.md`), 620px from the right on a desktop, with a
 * dirty check and a Save button. Two different shapes that happen to share
 * an English word.
 *
 * The header carries either a **Clear all** on the right or a **‹ Filters**
 * on the left, never both: the first is the top sheet, the second is a sheet
 * that was pushed over one and has somewhere to go back to.
 *
 * It is not `EditorShell`. That one folds a popover into a sheet for the quote
 * builder's editors and owns the desktop half too; here the desktop half is
 * already a `Popover` (the quick filter) or a `Modal` (the builder), and what
 * is wanted is only the phone box. Sharing the *shell* rather than the
 * decision keeps each surface's desktop shape its own.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconChevronLeft, IconClose } from '../frame/icons.js'
import { Button } from './primitives.js'
import { cn } from '../lib/cn.js'

export interface PanelSheetProps {
  open: boolean
  onClose: () => void
  /** "Filter", or the field a pushed sheet is about. */
  title: string
  /** The live count under it — "118 of 1,240 match · live". */
  sub?: string
  /** The top-right action. Left out on a pushed sheet. */
  action?: ReactNode
  /** `‹ Filters`, when this sheet was pushed over another. */
  onBack?: () => void
  /**
   * What the back link is called — the name of the sheet it goes back to.
   *
   * It said "Filters" in the markup, because the filter was the only thing
   * that pushed a second level. Board 6 pushes four more out of List
   * settings, and `‹ Filters` over the Fields panel is a link that names
   * the wrong place.
   */
  backLabel?: string
  /** The sticky primary — "Show 118 jobs", "Done". */
  footer?: ReactNode
  /**
   * Full height rather than 88vh. The advanced builder is a form of six rows
   * that each hold three controls; a sheet showing two of them and a scrollbar
   * is a worse advanced builder than none.
   */
  tall?: boolean
  /**
   * The iPad's two-across sheet (`quote-build-refresh.md` qb-08 C): on the
   * page colour, so the cards inside it read as cards, the title and its sub
   * on one line, a 36px outlined ✕, and a white footer with room under it.
   */
  wide?: boolean
  /**
   * No ✕: the sheet ends with its own `action` (qb-10 · 6's **Done**) and its
   * back chevron, and a third way out in the corner would be a third answer.
   */
  hideClose?: boolean
  /** A pushed sheet sits over the one that pushed it. */
  layer?: number
  children: ReactNode
}

export function PanelSheet({
  open, onClose, title, sub, action, onBack, backLabel = 'Filters', footer, tall, wide, hideClose,
  layer = 60, children,
}: PanelSheetProps) {
  const box = useRef<HTMLDivElement>(null)

  /*
   * Escape closes, or goes back when there is somewhere to go back to — and
   * Tab stays inside (§6.1, "focus is trapped").
   *
   * The trap matters more here than it looks: the sheet covers most of the
   * screen but the page is still behind it, so without this, tabbing out of
   * the last row lands on a row of the table nobody can see, and a screen
   * reader walks straight out of the sheet into the list it is filtering.
   * Wrapping at both ends is the whole of it.
   */
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        if (onBack) onBack()
        else onClose()
        return
      }
      if (event.key !== 'Tab' || !box.current) return
      /*
       * No `offsetParent` check: it is the obvious way to skip a hidden
       * control and it is null for every element under jsdom, which has no
       * layout — so the trap would be correct in a browser and dead in every
       * test. The sheet's own markup holds no hidden controls, so the list
       * is the query.
       */
      const focusable = [...box.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )]
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      const here = document.activeElement
      if (event.shiftKey && (here === first || !box.current.contains(here))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && here === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onBack, onClose])

  /*
   * And focus starts inside it.
   *
   * Not on the first control — that would be the back chevron or the ✕, and
   * a sheet that opens with "close" selected is one keystroke from undoing
   * itself. The box itself takes it (`tabIndex={-1}`), so the next Tab lands
   * on the first row.
   */
  useEffect(() => {
    /*
     * Unless a control inside already asked for it: a sheet whose body is a
     * search (Choices, qb-10 · 6) puts the caret in the box with `autoFocus`,
     * and children mount first, so taking it back here left typing nowhere.
     */
    if (open && !box.current?.contains(document.activeElement)) box.current?.focus({ preventScroll: true })
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      style={{ zIndex: layer }}
      data-overlay-layer={layer}
      /* Board 6: ink at 18%. A 35% scrim over a sheet that covers most of the
         screen anyway reads as the lights going out. */
      className="fixed inset-0 flex flex-col justify-end bg-[rgba(29,29,26,0.18)]"
      onClick={onClose}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'flex flex-col overflow-hidden border-t border-line pb-safe',
          wide ? 'rounded-t-sheet-wide bg-bg' : 'rounded-t-sheet bg-surface',
          tall ? 'h-app' : 'max-h-sheet',
        )}
      >
        {/*
          * The grab handle. Decoration, not a control — the sheet is dragged
          * by nothing and dismissed by the scrim, the action or Escape — but
          * it is what says "this came up from the bottom and can go back
          * down", and every other sheet in the product draws one.
          */}
        <span aria-hidden className={cn('mx-auto shrink-0 rounded-chip bg-line-strong',
          wide ? 'mt-[9px] h-[5px] w-10' : 'mt-2 h-1 w-9')} />

        {/*
          * 56px minimum, board 6's table: the back link on the left and only
          * on a second level, the title at 17/700 with its sub underneath,
          * the text action and the ✕ on the right. The ✕ stays on a pushed
          * sheet now — a second level is not a modal step, and somebody who
          * has gone two deep into List settings should not have to come back
          * out through Group to shut it.
          */}
        <header className={cn('flex shrink-0 items-center',
          wide ? 'gap-3 px-6 pb-3 pt-2' : 'min-h-[56px] gap-2.5 px-4 py-2')}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              /* 36px of target, which is what a thumb needs and what the
                 board's "≥ 44 px" rule means once the 56px row is counted. */
              className="-ml-2 flex h-9 shrink-0 items-center gap-0.5 rounded-button pl-1.5 pr-2 text-cell font-semibold text-accent"
            >
              <IconChevronLeft size={15} />
              {backLabel}
            </button>
          )}
          <span className={cn('flex min-w-0', wide ? 'items-baseline gap-3' : 'flex-col')}>
            <b className="truncate text-[17px] font-bold leading-tight text-text">{title}</b>
            {sub && (
              <span className={cn('truncate leading-tight text-text-muted', wide ? 'text-[12.5px]' : 'text-[12px]')}>
                {sub}
              </span>
            )}
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {action}
            {!hideClose && <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              /* A 34px tinted circle, so the one control that ends the sheet
                 is a thing rather than a glyph floating in the corner. */
              className={cn('flex items-center justify-center rounded-chip text-text-muted',
                wide ? 'h-9 w-9 border border-line bg-surface' : 'h-[34px] w-[34px] bg-hover')}
            >
              <IconClose size={17} />
            </button>}
          </span>
        </header>

        <div className={cn('min-h-0 flex-1 overflow-y-auto', !wide && 'border-t border-line-faint')}>
          {children}
        </div>

        {footer && (
          /*
            * Sticky above the safe area, and 28px of it: a 48px button whose
            * bottom edge is the bottom edge of the screen is a button a
            * thumb reaching for the home gesture presses by accident.
            */
          <footer className={cn('flex shrink-0 items-center gap-2 border-t border-line',
            wide ? 'mt-3 bg-surface px-6 pb-[22px] pt-3.5' : 'bg-surface-alt px-4 pb-7 pt-2.5')}>
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * The sheet's primary — board 6's 48px button, and the one shape for all of
 * them.
 *
 * Every sheet in the product ends in one of these and each was writing its
 * own `size="big"` plus four classes, which is how "Show 422 contacts" came
 * out 44px tall under a header built to the board and "Done" came out 44px
 * with a different radius. The height and the radius belong to the sheet, so
 * they are stated once, here, next to it.
 */
export function SheetButton({
  children, onClick, variant = 'primary', disabled,
}: {
  children: ReactNode
  onClick: () => void
  variant?: 'primary' | 'default'
  /**
   * A footer whose form is not finished yet.
   *
   * Greyed rather than absent: the button is where somebody is looking, and
   * one that appears only once the form is complete is one they go hunting
   * for. `Button` has always taken this; the sheet's footer simply never
   * passed it on, so every sheet with an incomplete form had to guard inside
   * its own handler and say nothing on the control.
   */
  disabled?: boolean
}) {
  return (
    <Button
      variant={variant}
      size="big"
      disabled={disabled}
      onClick={onClick}
      className="ml-auto h-12 flex-1 justify-center gap-1.5 rounded-record"
    >
      {children}
    </Button>
  )
}
